import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase URL e Anon Key são obrigatórios');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Tipos
export interface User {
  id: string;
  email: string;
  email_confirmed_at?: string;
  full_name?: string;
  phone?: string;
  user_metadata: {
    full_name?: string;
    phone?: string;
    avatar_url?: string;
  };
  created_at: string;
}

export class AuthError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
  }
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  fullName: string;
  phone?: string;
}

// Serviço de Autenticação
export const authService = {
  // Login com email e senha
  async login(credentials: LoginCredentials): Promise<{ user: User | null; error: AuthError | null }> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        return {
          user: null,
          error: new AuthError(translateAuthError(error.message), error.status),
        };
      }

      return {
        user: data.user as unknown as User,
        error: null,
      };
    } catch (err: any) {
      return {
        user: null,
        error: new AuthError(err.message || 'Erro ao fazer login. Tente novamente.'),
      };
    }
  },

  // Registro de novo utilizador
  async register(data: RegisterData): Promise<{ user: User | null; error: AuthError | null }> {
    try {
      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.fullName,
            phone: data.phone || '',
          },
          emailRedirectTo: `${window.location.origin}/area-cliente`,
        },
      });

      if (error) {
        return {
          user: null,
          error: new AuthError(translateAuthError(error.message), error.status),
        };
      }

      return {
        user: authData.user as unknown as User,
        error: null,
      };
    } catch (err: any) {
      return {
        user: null,
        error: new AuthError(err.message || 'Erro ao criar conta. Tente novamente.'),
      };
    }
  },

  // Logout
  async logout(): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        return {
          error: new AuthError(translateAuthError(error.message)),
        };
      }

      return { error: null };
    } catch (err: any) {
      return {
        error: new AuthError(err.message || 'Erro ao fazer logout. Tente novamente.'),
      };
    }
  },

  // Recuperação de senha
  async resetPassword(email: string): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/area-cliente/redefinir-senha`,
      });

      if (error) {
        return {
          error: new AuthError(translateAuthError(error.message)),
        };
      }

      return { error: null };
    } catch (err: any) {
      return {
        error: new AuthError(err.message || 'Erro ao enviar email de recuperação. Tente novamente.'),
      };
    }
  },

  // Atualizar senha
  async updatePassword(newPassword: string, _currentPassword?: string): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        return {
          error: new AuthError(translateAuthError(error.message)),
        };
      }

      return { error: null };
    } catch (err: any) {
      return {
        error: new AuthError(err.message || 'Erro ao atualizar senha. Tente novamente.'),
      };
    }
  },

  // Obter utilizador atual
  async getCurrentUser(): Promise<User | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user as User | null;
    } catch (err) {
      return null;
    }
  },

  // Obter sessão atual
  async getSession() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session;
    } catch (err) {
      return null;
    }
  },

  // Atualizar perfil do utilizador
  async updateProfile(updates: {
    full_name?: string;
    phone?: string;
    avatar_url?: string;
  }): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.updateUser({
        data: updates,
      });

      if (error) {
        return {
          error: new AuthError(translateAuthError(error.message)),
        };
      }

      return { error: null };
    } catch (err: any) {
      return {
        error: new AuthError(err.message || 'Erro ao atualizar perfil. Tente novamente.'),
      };
    }
  },

  // Login com Google
  async loginWithGoogle(): Promise<{ user: User | null; error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/area-cliente`,
        },
      });

      if (error) {
        return {
          user: null,
          error: new AuthError(translateAuthError(error.message), error.status),
        };
      }

      return { user: null, error: null };
    } catch (err) {
      return {
        user: null,
        error: new AuthError('Erro ao fazer login com Google. Tente novamente.'),
      };
    }
  },

  // Login com Facebook
  async loginWithFacebook(): Promise<{ user: User | null; error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${window.location.origin}/area-cliente`,
        },
      });

      if (error) {
        return {
          user: null,
          error: new AuthError(translateAuthError(error.message), error.status),
        };
      }

      return { user: null, error: null };
    } catch (err) {
      return {
        user: null,
        error: new AuthError('Erro ao fazer login com Facebook. Tente novamente.'),
      };
    }
  },

  // Login com Apple
  async loginWithApple(): Promise<{ user: User | null; error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/area-cliente`,
        },
      });

      if (error) {
        return {
          user: null,
          error: new AuthError(translateAuthError(error.message), error.status),
        };
      }

      return { user: null, error: null };
    } catch (err) {
      return {
        user: null,
        error: new AuthError('Erro ao fazer login com Apple. Tente novamente.'),
      };
    }
  },

  // Verificar email
  async verifyEmail(_token?: string): Promise<{ error: AuthError | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { error: new AuthError('Utilizador não encontrado') };
      }
      return { error: null };
    } catch (err) {
      return { error: new AuthError('Erro ao verificar email. Tente novamente.') };
    }
  },

  // Reenviar email de verificação
  async resendVerificationEmail(email: string): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });

      if (error) {
        return { error: new AuthError(translateAuthError(error.message)) };
      }

      return { error: null };
    } catch (err) {
      return { error: new AuthError('Erro ao reenviar email de verificação. Tente novamente.') };
    }
  },

  // Verificar se email existe
  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password: 'invalid_password_check_12345',
      });

      if (error && error.message.includes('Invalid login credentials')) {
        return true;
      }

      return false;
    } catch (err) {
      return false;
    }
  },

  // Reenviar email de confirmação
  async resendConfirmationEmail(email: string): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });

      if (error) {
        return { error: new AuthError(translateAuthError(error.message)) };
      }

      return { error: null };
    } catch (err) {
      return { error: new AuthError('Erro ao reenviar email de confirmação. Tente novamente.') };
    }
  },
};

// Atualizar senha
export const updatePassword = async (newPassword: string): Promise<{ error: AuthError | null }> => {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      return {
        error: new AuthError(translateAuthError(error.message)),
      };
    }

    return { error: null };
  } catch (err: any) {
    return {
      error: new AuthError(err.message || 'Erro ao atualizar senha. Tente novamente.'),
    };
  }
};

// Traduzir erros de autenticação para português
function translateAuthError(errorMessage: string): string {
  const errorMap: Record<string, string> = {
    'Invalid login credentials': 'Email ou palavra-passe incorretos',
    'Email not confirmed': 'Email não confirmado. Verifique sua caixa de entrada.',
    'User already registered': 'Este email já está registado',
    'Password should be at least 6 characters': 'A palavra-passe deve ter pelo menos 6 caracteres',
    'Unable to validate email address: invalid format': 'Formato de email inválido',
    'Signup requires a valid password': 'É necessária uma palavra-passe válida',
    'User not found': 'Utilizador não encontrado',
    'Email rate limit exceeded': 'Limite de emails excedido. Tente novamente mais tarde.',
    'Invalid email or password': 'Email ou palavra-passe inválidos',
    'Email link is invalid or has expired': 'Link de email inválido ou expirado',
    'Token has expired or is invalid': 'Token expirado ou inválido',
    'New password should be different from the old password': 'A nova palavra-passe deve ser diferente da anterior',
  };

  return errorMap[errorMessage] || errorMessage;
}

// Hook para ouvir mudanças de autenticação
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user as User | null);
  });
};
